/*
 * Copyright 2021 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter, __generator } from "tslib";
import { IconNames } from "./constants";
import { wrapWithTimer } from "./loaderUtils";
/**
 * The default icon contents loader implementation, optimized for webpack.
 *
 * @see https://webpack.js.org/api/module-methods/#magic-comments for dynamic import() reference
 */
var defaultIconContentsLoader = function (name) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, import(
                /* webpackInclude: /\.js$/ */
                /* webpackMode: "lazy-once" */
                "./generated/components/" + name)];
            case 1: return [2 /*return*/, (_a.sent()).default];
        }
    });
}); };
/**
 * Blueprint icons loader.
 */
var Icons = /** @class */ (function () {
    function Icons() {
        /** @internal */
        this.loadedIcons = new Map();
    }
    Icons.load = function (icons, options) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!Array.isArray(icons)) {
                            icons = [icons];
                        }
                        return [4 /*yield*/, Promise.all(icons.map(function (icon) { return _this.loadImpl(icon, options); }))];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Load all available icons for use in Blueprint components.
     */
    Icons.loadAll = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var allIcons;
            var _this = this;
            return __generator(this, function (_a) {
                allIcons = Object.values(IconNames);
                wrapWithTimer("[Blueprint] loading all icons", function () { return _this.load(allIcons, options); });
                return [2 /*return*/];
            });
        });
    };
    /**
     * Get the icon SVG component.
     */
    Icons.getComponent = function (icon) {
        if (!this.isValidIconName(icon)) {
            // don't warn, since this.load() will have warned already
            return undefined;
        }
        else if (!singleton.loadedIcons.has(icon)) {
            console.error("[Blueprint] Icon '" + icon + "' not loaded yet, did you call Icons.load('" + icon + "')?");
            return undefined;
        }
        return singleton.loadedIcons.get(icon);
    };
    Icons.loadImpl = function (icon, options) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var load, component, e_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!this.isValidIconName(icon)) {
                            console.error("[Blueprint] Unknown icon '" + icon + "'");
                            return [2 /*return*/];
                        }
                        else if (singleton.loadedIcons.has(icon)) {
                            // already loaded, no-op
                            return [2 /*return*/];
                        }
                        load = (_a = options === null || options === void 0 ? void 0 : options.loader) !== null && _a !== void 0 ? _a : defaultIconContentsLoader;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, load(icon)];
                    case 2:
                        component = _b.sent();
                        singleton.loadedIcons.set(icon, component);
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _b.sent();
                        console.error("[Blueprint] Unable to load icon '" + icon + "'", e_1);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    Icons.isValidIconName = function (icon) {
        var allIcons = Object.values(IconNames);
        return allIcons.indexOf(icon) >= 0;
    };
    return Icons;
}());
export { Icons };
var singleton = new Icons();
//# sourceMappingURL=iconLoader.js.map